import { combineReducers } from 'redux';
import _ from 'lodash';
import axios from 'axios';
import i18next from 'i18next';
import humps from 'humps';

import { getItem, mergeItemFields } from 'src/reducers/common/itemReducer';
import { FETCH_EMPLOYEE_CATEGORIES, FETCH_TOTAL_EMPLOYEE, FETCH_MONTHLY_EXPENSES, FETCH_EXPENSE_SUMMARY_BY_YEAR } from 'src/constants/EndpointUrls';
import { setGlobalErrors } from 'src/reducers/global';
import { generateExpensesSummaryChartData, checkCompareYear, generateChartColor, filterExpensesData } from 'src/utils/Dashboard';

import createNamedWrapperReducer from '../createNamedWrapperReducer';
import { listReducer, itemReducer } from '../common';

export const REDUCER_NAME = 'dashboard';

/**
 * Retrieves specific chart data based on the provided key from the state.
 *
 * @param {Object} state - The current state of the application.
 * @param {string} key - The key to identify the specific chart data.
 * @returns {Object} The chart data corresponding to the key, or an empty object if not found.
 */
export const getChartData = (state, key) => _.get(getItem(REDUCER_NAME, state), key, []);

/**
 * Retrieves the chart data based on the provided key from the state.
 *
 * @param {string} key - The key to access the specific data in the chart.
 * @returns {Object} The chart data corresponding to the provided key, or an empty object if not found.
 */
export const setDashboardData = (key, object = {}) => dispatch => dispatch(mergeItemFields(REDUCER_NAME, { [key]: object }));

export const fetchTotalEmployee = month => async dispatch => {
  try {
    const params = { target_month: month };
    const response = await axios.get(FETCH_TOTAL_EMPLOYEE, { params });
    dispatch(setDashboardData('totalEmployee', response.data));
  } catch (error) {
    dispatch(setGlobalErrors(i18next.t('reducers.dashboard.an_error_occurred')));
  }
};

export const fetchEmployeeCategories = category => async dispatch => {
  try {
    const params = { target_categories: category };
    const response = await axios.get(FETCH_EMPLOYEE_CATEGORIES, { params });
    if (response.status === 200) {
      const chartWithColors = generateChartColor(response.data);
      dispatch(setDashboardData('employeeCategory', chartWithColors));
    }
  } catch (error) {
    dispatch(setGlobalErrors(i18next.t('reducers.dashboard.an_error_occurred')));
  }
};

export const fetchMonthlyExpenses = targetMonth => async dispatch => {
  try {
    const params = { target_month: targetMonth };
    const response = await axios.get(FETCH_MONTHLY_EXPENSES, { params });
    if (response.status === 200) {
      dispatch(setDashboardData('expensesMonthly', filterExpensesData(response.data)));
    }
  } catch (error) {
    dispatch(setGlobalErrors(i18next.t('reducers.dashboard.an_error_occurred')));
  }
};

export const fetchExpenseSummary = (selectedYear, viewMode = 'Monthly') => async dispatch => {
  try {
    const params = { target_year: selectedYear.targetYear, ...(viewMode !== 'Monthly' && { compare_year: selectedYear.compareYear }) };
    const compareYear = checkCompareYear(params, viewMode);

    const response = await axios.get(FETCH_EXPENSE_SUMMARY_BY_YEAR, { params: compareYear });
    if (response.status === 200) {
      const data = generateExpensesSummaryChartData(response.data, humps.camelizeKeys(compareYear), viewMode);
      dispatch(setDashboardData('expenseSummary', data));
    }
  } catch (error) {
    dispatch(setGlobalErrors(i18next.t('reducers.dashboard.an_error_occurred')));
  }
};

export default combineReducers({
  list: createNamedWrapperReducer(listReducer, REDUCER_NAME),
  item: createNamedWrapperReducer(itemReducer, REDUCER_NAME)
});
